import { NgClass } from '@angular/common'
import { Component, EventEmitter, inject, Input, LOCALE_ID, NgZone, OnChanges, OnInit, Optional, Output, SimpleChanges, ViewEncapsulation } from '@angular/core'
import { ControlContainer, NgForm, FormsModule } from '@angular/forms'

import { PrimeTemplate } from 'primeng/api'
import { AutoComplete } from 'primeng/autocomplete'
import { isNil } from 'lodash-es'

import { Place, toPlaceForm } from '@app/interfaces/place'
import { PlacesApi } from '@app/apis/places.api'
import { DetailMode } from '@app/components/generic/generic.constants'
import type { CountryListItem } from '@app/generated/api/model'
import { RequiredFieldTextComponent } from '../required-field-text.component'

@Component({
  selector: 'app-program-place-field',
  templateUrl: 'program-place-field.component.html',
  styleUrl: 'program-place-field.component.less',
  encapsulation: ViewEncapsulation.None,
  viewProviders: [{
    provide: ControlContainer,
    deps: [[Optional, NgForm]],
    useFactory: (ngForm: NgForm) => ngForm,
  }],
  imports: [
    AutoComplete,
    FormsModule,
    NgClass,
    PrimeTemplate,
    RequiredFieldTextComponent,
  ],
})
export class ProgramPlaceFieldComponent implements OnInit, OnChanges {
  public readonly placesApi = inject(PlacesApi)
  public readonly zone = inject(NgZone)
  public readonly locale = inject(LOCALE_ID)

  @Input() country?: Pick<CountryListItem, 'id' | 'code_iso' | 'code_iso_2' | 'label_en' | 'label_fr'> | null
  @Input() programPlace?: Place | null
  @Input() required: boolean = false
  @Input() recommended: boolean = false
  @Input() mode: string = 'normal'
  @Input() tabindex: number = -1

  @Output() programPlaceChange = new EventEmitter<Place | null | undefined>()

  public allPlaces: Place[] = []

  public filterLabelField: string = 'label_en'
  protected readonly DetailMode = DetailMode

  ngOnInit (): void {
    if (this.locale === 'fr') {
      this.filterLabelField = 'label_fr'
    }
  }

  ngOnChanges (changes: SimpleChanges<ProgramPlaceFieldComponent>) {
    // When the country is changed, and we need to reset the Place to null
    const onlyCountryChange = changes.country && !changes.programPlace

    // If only the country changed (the user selected a new country in the list) then we set the place to null
    // because it's no longer valid for the new country
    if (onlyCountryChange) {
      this.programPlace = null
      // Notify the metadata parent that the Place is changed (null)
      // This needs to be done in the following digest cycle to avoid the "Expression has changed after it was checked" warning,
      // because otherwise we will trigger another change from this change function, which is not nice.
      setTimeout(() => {
        this.programPlaceChange.emit(this.programPlace)
      })
    }
  }

  /**
   * Return project codes matching the given code using an `icontains` matcher.
   * This is necessary because we cannot present a list of *all* projects: it must be pre-filtered.
   */
  getPlaceSuggestions ($event: { query: string, originalEvent: any }) {
    this.placesApi.list({ limit: 10000, ordering: 'admin_zone_2__adm2_name,name', admin_zone_2__admin_zone_1__country__id: this.country?.id, name: $event.query }).subscribe(
      (res) => {
        this.allPlaces = res.results.map(toPlaceForm)
      })
  }

  /**
   * return whether the field's value is undefined or empty
   */
  isEmpty () {
    return isNil(this.programPlace)
  }
}
